import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Loader2,
  Sparkles,
  Upload,
  X,
  Check,
  Copy,
  Download,
  Printer,
  Lock,
  Trash2,
  Play,
} from 'lucide-react';
// Caso 05 v1 — Reusable celebration overlay for the "Test celebration" button.
import { CelebrationOverlay } from './CelebrationOverlay';
import { uploadImage } from '@/lib/images/upload';
import { resolveImageUrl } from '@/lib/images/resolveImageUrl';
import type { CompressPreset } from '@/lib/images/compress';
import { brandsApi, brandkitApi } from '@/features/brands/api';
// PRD §18 — mini animated previews for the Game tab picker.
import { GamePreview } from './tabs/GamePreviews';
import { queryKeys } from '@/lib/api/queryKeys';
import type { CoCompany, XkBrand } from '@/lib/api/types';
import { useCurrentCompany } from '@/lib/branding/useCurrentBrand';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs';
import { Skeleton } from '@/components/ui/Skeleton';
import { toast } from '@/components/ui/Toast';
import { THEME, BRAND_DEFAULTS } from '@/lib/theme';
import { useT } from '@/lib/i18n/useT';
import { useCurrentPlan } from '@/lib/permissions/usePlanQuota';
import { useFeatureAccess } from '@onxite/plan-sdk';
// PRD §13.5 — TCPA acknowledgement is required before enabling Variant B
// (PII + unbundled marketing opt-in) on the consumer flow.
import { LegalAcceptanceModal } from '@/features/legal/LegalAcceptanceModal';
import { TCPA_ACK } from '@/features/legal/documents';
import { legalApi } from '@/features/legal/api';

// ════════════════════════════════════════════════════════════════════════════
// Config — mirrors `Xotski-v2/Brand Studio · Default` BrandStudioConfig
// ════════════════════════════════════════════════════════════════════════════

const FONTS = [
  'Inter',
  'DM Sans',
  'Space Grotesk',
  'Outfit',
  'Plus Jakarta Sans',
  'Montserrat',
  'Bebas Neue',
  'Sora',
  'Roboto',
  'Poppins',
  'Lato',
  'Open Sans',
] as const;

const CELEBRATIONS = ['confetti', 'fireworks', 'sparks', 'soccer', 'none'] as const;
const BUTTON_STYLES = ['rounded', 'square', 'pill'] as const;
const AGE_GATES = ['none', '18+', '21+'] as const;

interface GameInfo {
  id: string;
  nameKey: string;
  tier: 'free' | 'premium' | 'pro';
  descKey: string;
  specKeys: [string, string, string];
}
const GAMES: GameInfo[] = [
  {
    id: 'wheel',
    nameKey: 'brandStudio.game.wheel.name',
    tier: 'free',
    descKey: 'brandStudio.game.wheel.desc',
    specKeys: [
      'brandStudio.game.wheel.s1',
      'brandStudio.game.wheel.s2',
      'brandStudio.game.wheel.s3',
    ],
  },
  {
    id: 'scratch',
    nameKey: 'brandStudio.game.scratch.name',
    tier: 'premium',
    descKey: 'brandStudio.game.scratch.desc',
    specKeys: [
      'brandStudio.game.scratch.s1',
      'brandStudio.game.scratch.s2',
      'brandStudio.game.scratch.s3',
    ],
  },
  {
    id: 'pirinola',
    nameKey: 'brandStudio.game.pirinola.name',
    tier: 'premium',
    descKey: 'brandStudio.game.pirinola.desc',
    specKeys: [
      'brandStudio.game.pirinola.s1',
      'brandStudio.game.pirinola.s2',
      'brandStudio.game.pirinola.s3',
    ],
  },
  {
    id: 'random-draw',
    nameKey: 'brandStudio.game.random.name',
    tier: 'premium',
    descKey: 'brandStudio.game.random.desc',
    specKeys: [
      'brandStudio.game.random.s1',
      'brandStudio.game.random.s2',
      'brandStudio.game.random.s3',
    ],
  },
  {
    id: 'plinko',
    nameKey: 'brandStudio.game.plinko.name',
    tier: 'pro',
    descKey: 'brandStudio.game.plinko.desc',
    specKeys: [
      'brandStudio.game.plinko.s1',
      'brandStudio.game.plinko.s2',
      'brandStudio.game.plinko.s3',
    ],
  },
  {
    id: 'soccer',
    nameKey: 'brandStudio.game.soccer.name',
    tier: 'pro',
    descKey: 'brandStudio.game.soccer.desc',
    specKeys: [
      'brandStudio.game.soccer.s1',
      'brandStudio.game.soccer.s2',
      'brandStudio.game.soccer.s3',
    ],
  },
  {
    id: 'loteria',
    nameKey: 'brandStudio.game.loteria.name',
    tier: 'pro',
    descKey: 'brandStudio.game.loteria.desc',
    specKeys: [
      'brandStudio.game.loteria.s1',
      'brandStudio.game.loteria.s2',
      'brandStudio.game.loteria.s3',
    ],
  },
  {
    id: 'slots',
    nameKey: 'brandStudio.game.slots.name',
    tier: 'premium',
    descKey: 'brandStudio.game.slots.desc',
    specKeys: [
      'brandStudio.game.slots.s1',
      'brandStudio.game.slots.s2',
      'brandStudio.game.slots.s3',
    ],
  },
];

// PRD §9 v2 — `MOCK_INVENTORY` was replaced by real backend data. Brand
// Studio Prizes tab now reads xk_stock rows scoped to the brand and
// allows adopting prizes from the parent agency's catalogue via
// `stockApi.agencyCatalog` + `stockApi.adopt`. See PrizesTab below.

interface BrandStudioConfig {
  // Identity
  name: string;
  industry: string;
  campaignName: string;
  brandLogo: string | null;
  qrThumbnail: string | null;
  backgroundImage: string | null;
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  backgroundColor: string;
  textColor: string;
  font: string;
  // Experience
  language: 'en' | 'es' | 'en-es';
  collectData: boolean;
  collectName: boolean;
  collectEmail: boolean;
  collectPhone: boolean;
  ageGate: (typeof AGE_GATES)[number];
  demoAge: boolean;
  demoGender: boolean;
  // PRD §8 Tab 2 — inclusive protocol pronouns toggle (free tier).
  demoPronouns: boolean;
  demoEthnicity: boolean;
  demoNationality: boolean;
  // PRD §8 Tab 2 — number of custom questions added (gated by plan tier
  // 0/2/5/5/inf for Pilot/Scale/Program/Enterprise/...). The list itself
  // ships in Phase 4; this field carries the count so the quota chip can
  // render today.
  customQuestionsCount: number;
  // Look & Feel
  buttonStyle: (typeof BUTTON_STYLES)[number];
  celebrationTheme: (typeof CELEBRATIONS)[number];
  consentWording: string;
  // PRD §11 Screen 3 — consent flow variant.
  //   'A' = anonymous (no PII, no marketing checkbox)
  //   'B' = PII collection + unbundled marketing opt-in (default)
  consentVariant: 'A' | 'B';
  // PRD §16 — Atledi voice preset that shapes the recap + welcome
  // narrative. Stored at the brand level so every event the brand runs
  // inherits the same voice unless overridden per-event.
  atlediTone: 'playful' | 'professional' | 'warm' | 'bold' | 'custom';
  atlediToneCustom: string;
  gameCTAEn: string;
  gameCTAEs: string;
  // PRD §11 Screen 6 — post-claim redirect mode.
  //   'none'     = Onxite finish screen (default)
  //   'branded'  = stay in Xotski, show branded thank-you panel
  //   'external' = redirect to postClaimRedirectUrl
  postClaimRedirect: 'none' | 'branded' | 'external';
  postClaimRedirectUrl: string;
  // Game
  gameMechanic: string;
  gameTier: 'free' | 'premium' | 'pro';
  wheelTaglineEn: string;
  wheelTaglineEs: string;
  wheelHeroImage: string | null;
  // PRD §9 v2 — Prizes no longer live on this local config blob; they are
  // persisted as `xk_stock` rows owned by the brand and fetched by
  // `PrizesTab` from the backend on render.
}

function configFromBrand(b: XkBrand): BrandStudioConfig {
  return {
    ...configFromCompany(b),
    name: b.name ?? '',
  };
}

function configFromCompany(c: CoCompany | null): BrandStudioConfig {
  return {
    name: '',
    industry: c?.industry ?? '',
    campaignName: '',
    brandLogo: c?.logo ?? null,
    qrThumbnail: null,
    backgroundImage: c?.backgroundImage ?? null,
    primaryColor: c?.backgroundColor ?? BRAND_DEFAULTS.primaryColor,
    secondaryColor: c?.secondaryColor ?? THEME.navy2,
    accentColor: c?.secondaryColor ?? BRAND_DEFAULTS.accentColor,
    backgroundColor: c?.backgroundColor ?? BRAND_DEFAULTS.primaryColor,
    textColor: c?.fontColor ?? '#FFFFFF',
    font: BRAND_DEFAULTS.font,
    language: 'en',
    collectData: true,
    collectName: true,
    collectEmail: true,
    collectPhone: false,
    ageGate: 'none',
    demoAge: true,
    demoGender: true,
    demoPronouns: false,
    demoEthnicity: false,
    demoNationality: false,
    customQuestionsCount: 0,
    buttonStyle: 'rounded',
    celebrationTheme: 'confetti',
    consentWording:
      'By continuing, you agree to our Terms of Service and Privacy Policy. Your data may be shared with the event sponsor.',
    consentVariant: 'B',
    atlediTone: 'warm',
    atlediToneCustom: '',
    postClaimRedirect: 'none',
    postClaimRedirectUrl: '',
    gameCTAEn: 'Spin to win!',
    gameCTAEs: '¡Gira para ganar!',
    gameMechanic: 'wheel',
    gameTier: 'free',
    wheelTaglineEn: '',
    wheelTaglineEs: '',
    wheelHeroImage: null,
  };
}

// ════════════════════════════════════════════════════════════════════════════
// Page
// ════════════════════════════════════════════════════════════════════════════

// Prizes used to live in Brand Studio; moved to the Brand Profile
// (/brand/:id) Tabs. Brand Studio now focuses on identity/experience/game.
const TAB_LABELS = ['Identity', 'Experience', 'Look & Feel', 'Game', 'Output'] as const;
type TabId = (typeof TAB_LABELS)[number];

const TAB_LABEL_KEYS: Record<TabId, string> = {
  Identity: 'brandStudio.tab.identity',
  Experience: 'brandStudio.tab.experience',
  'Look & Feel': 'brandStudio.tab.lookFeel',
  Game: 'brandStudio.tab.game',
  Output: 'brandStudio.tab.output',
};

export function BrandStudioNewPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const t = useT();
  const { id } = useParams<{ id?: string }>();
  const brandId = id ? Number(id) : null;
  const isEdit = Boolean(brandId);
  const { company, isLoading: companyLoading } = useCurrentCompany();
  const brandQ = useQuery({
    queryKey: queryKeys.brands.detail(brandId ?? 0),
    queryFn: () => brandsApi.get(brandId!),
    enabled: isEdit,
  });
  const [config, setConfig] = useState<BrandStudioConfig | null>(null);
  const [tab, setTab] = useState<TabId>('Identity');
  const [confirmDelete, setConfirmDelete] = useState(false);
  // Tracks how many ImagePicker children are currently uploading. Must live
  // ABOVE the `if (!config) return <Skeleton>` early-return at line 302 —
  // declaring it later would change hook count between renders and React
  // would throw "Rendered more hooks than during the previous render" as
  // soon as the query resolved.
  const [uploadingCount, setUploadingCount] = useState(0);

  // Seed once when source (brand or company) loads.
  useEffect(() => {
    if (config) return;
    if (isEdit && brandQ.data) setConfig(configFromBrand(brandQ.data));
    else if (!isEdit && company) setConfig(configFromCompany(company));
  }, [company, brandQ.data, config, isEdit]);

  /**
   * PRD §8 Tab 1 — persist the full 5-color palette + experience defaults
   * to CoBrandKit. The kit is the single source of truth that the Xotski
   * bs_event snapshot reads on associate-survey and reset-identity. We
   * always upsert to the xotski-scoped variant.
   */
  const persistBrandKit = async (brand: { id: number }) => {
    if (!config) return;
    try {
      await brandkitApi.upsert(brand.id, {
        appType: 'xotski',
        campaignName: config.campaignName || undefined,
        primaryColor: config.primaryColor,
        secondaryColor: config.secondaryColor,
        accentColor: config.accentColor,
        backgroundColor: config.backgroundColor,
        fontColor: config.textColor,
        font: config.font,
        brandLogo: config.brandLogo ?? undefined,
        backgroundImage: config.backgroundImage ?? undefined,
        // Experience defaults (Tab 2) — flow into bs_event.create snapshot.
        language: config.language as any,
        collectName: config.collectName,
        collectEmail: config.collectEmail,
        collectPhone: config.collectPhone,
        ageGate: config.ageGate as any,
        demoAge: config.demoAge,
        demoGender: config.demoGender,
        demoEthnicity: config.demoEthnicity,
        demoNationality: config.demoNationality,
        celebrationTheme: config.celebrationTheme as any,
      } as any);
    } catch (e) {
      toast({
        tone: 'error',
        title: t('brandStudio.kitSaveFailed'),
        description: (e as Error).message,
      });
    }
  };

  const create = useMutation({
    mutationFn: (body: Partial<CoCompany>) => brandsApi.create(body),
    onSuccess: async (brand) => {
      await persistBrandKit(brand);
      queryClient.invalidateQueries({ queryKey: queryKeys.brands.all });
      queryClient.invalidateQueries({
        queryKey: queryKeys.companies.managers(company?.id ?? 0),
      });
      toast({ tone: 'success', title: t('brandStudio.brandCreated', { name: brand.name }) });
      navigate(`/brand/${brand.id}`);
    },
    onError: (e: Error) =>
      toast({ tone: 'error', title: t('brandStudio.createFailed'), description: e.message }),
  });

  const updateBrand = useMutation({
    mutationFn: (body: Partial<CoCompany>) => brandsApi.update(brandId!, body),
    onSuccess: async (brand) => {
      await persistBrandKit(brand);
      queryClient.invalidateQueries({ queryKey: queryKeys.brands.detail(brand.id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.brands.all });
      toast({ tone: 'success', title: t('brandStudio.brandUpdated', { name: brand.name }) });
      navigate(`/brand/${brand.id}`);
    },
    onError: (e: Error) =>
      toast({ tone: 'error', title: t('brandStudio.updateFailed'), description: e.message }),
  });

  const removeBrand = useMutation({
    mutationFn: () => brandsApi.remove(brandId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.brands.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.eventGroups.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.events.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.stations.all });
      toast({ tone: 'success', title: t('brandStudio.brandDeleted', { name: brandQ.data?.name ?? '' }) });
      navigate('/');
    },
    onError: (e: Error) =>
      toast({ tone: 'error', title: t('brandStudio.deleteFailed'), description: e.message }),
  });

  const loading = isEdit ? brandQ.isLoading : companyLoading;
  if (loading || !config) {
    return (
      <div className="page">
        <Skeleton className="mb-4 h-9 w-72 rounded" />
        <Skeleton className="h-96 w-full rounded-xl" />
      </div>
    );
  }

  const update = (delta: Partial<BrandStudioConfig>) =>
    setConfig((c) => (c ? { ...c, ...delta } : c));

  const saving = create.isPending || updateBrand.isPending;
  const pending = saving || removeBrand.isPending;

  /**
   * Brand form errors. Computed every render so the save button can disable
   * cleanly. Hex-color validation tolerates both #RGB and #RRGGBB. Logo and
   * background image are optional (they fall back to inherited values from
   * the parent company).
   */
  const HEX_COLOR = /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/;
  const brandErrors: { name?: string; primaryColor?: string; secondaryColor?: string; textColor?: string } = {};
  if (config.name.trim().length < 2) brandErrors.name = t('brandStudio.error.nameRequired');
  if (config.primaryColor && !HEX_COLOR.test(config.primaryColor)) {
    brandErrors.primaryColor = t('brandStudio.error.primaryHex');
  }
  if (config.secondaryColor && !HEX_COLOR.test(config.secondaryColor)) {
    brandErrors.secondaryColor = t('brandStudio.error.secondaryHex');
  }
  if (config.textColor && !HEX_COLOR.test(config.textColor)) {
    brandErrors.textColor = t('brandStudio.error.textHex');
  }
  // ImagePicker children call this while uploading to /uploads. We disable
  // Save during the upload so the payload always carries the fresh URL, not
  // a stale value (and never a raw data URI — see ImagePicker below).
  const onUploadingChange = (uploading: boolean) =>
    setUploadingCount((n) => Math.max(0, n + (uploading ? 1 : -1)));
  const isUploading = uploadingCount > 0;
  const canSave =
    Object.keys(brandErrors).length === 0 && !pending && !isUploading;

  const onSave = () => {
    if (!canSave) return;
    const payload: Partial<CoCompany> = {
      name: config.name.trim(),
      logo: config.brandLogo,
      backgroundColor: config.primaryColor,
      secondaryColor: config.secondaryColor,
      fontColor: config.textColor,
      backgroundImage: config.backgroundImage,
      industry: config.industry || null,
    };
    if (isEdit) updateBrand.mutate(payload);
    else create.mutate(payload);
  };

  return (
    <div className="page">
      {/* ── Page header ─────────────────────────────────────────────── */}
      <header className="mb-6 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="grid h-8 w-8 place-items-center rounded-md border border-[var(--color-xk-border)] text-[var(--color-xk-text-muted)] hover:text-[var(--color-xk-lavender)]"
            title={t('brandStudio.back')}
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <div>
            <h1
              className="flex items-center gap-2 text-2xl font-black tracking-tight text-[var(--color-xk-lavender)]"
              style={{ fontFamily: 'var(--font-display)' }}
            >
              <Sparkles className="h-5 w-5 text-[var(--color-xk-accent)]" />
              {t('brandStudio.title')}
            </h1>
            <p className="text-[13px] text-[var(--color-xk-text-muted)]">
              {isEdit ? (
                <>
                  {t('brandStudio.editing')}{' '}
                  <span className="text-[var(--color-xk-lavender)]">
                    {brandQ.data?.name ?? '…'}
                  </span>
                </>
              ) : (
                <>
                  {t('brandStudio.newBrandInherit')}{' '}
                  <span className="text-[var(--color-xk-lavender)]">{company?.name}</span>
                </>
              )}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isEdit && (
            <button
              type="button"
              onClick={() =>
                confirmDelete ? removeBrand.mutate() : setConfirmDelete(true)
              }
              disabled={pending}
              className="inline-flex items-center gap-1.5 rounded-lg border border-[var(--color-error)] px-3 py-2 text-xs font-extrabold uppercase tracking-widest text-[var(--color-error)] transition-colors hover:bg-[rgb(239_68_68/0.1)] disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Trash2 className="h-3.5 w-3.5" />
              {removeBrand.isPending
                ? t('brandStudio.deleting')
                : confirmDelete
                  ? t('brandStudio.confirmDelete')
                  : t('brandStudio.deleteBrand')}
            </button>
          )}
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="rounded-lg border border-[var(--color-xk-border)] px-4 py-2 text-xs font-bold text-[var(--color-xk-lavender)] hover:bg-[var(--color-xk-surface)]"
          >
            {t('brandStudio.cancel')}
          </button>
          <button
            type="button"
            disabled={!canSave}
            onClick={onSave}
            className="inline-flex items-center gap-1.5 rounded-lg bg-[var(--color-xk-accent)] px-5 py-2 text-xs font-extrabold text-[#0A0A0A] transition-colors hover:bg-[var(--color-xk-accent-hover)] disabled:cursor-not-allowed disabled:opacity-50"
          >
            {saving ? t('brandStudio.saving') : isEdit ? t('brandStudio.saveChanges') : t('brandStudio.savePublish')}
          </button>
        </div>
      </header>

      {isEdit && confirmDelete && (
        <div className="mb-4 rounded-lg border border-[var(--color-error)] bg-[rgb(239_68_68/0.08)] p-3 text-xs text-[var(--color-fg-primary)]">
          <strong className="text-[var(--color-error)]">{t('brandStudio.confirmDeleteTitle')}</strong> {t('brandStudio.confirmDeleteBody')}{' '}
          <em>{t('brandStudio.confirmDeleteEmph')}</em> {t('brandStudio.confirmDeleteEnd')}
        </div>
      )}

      {Object.keys(brandErrors).length > 0 && (
        <div className="mb-4 rounded-lg border border-[var(--color-error)]/40 bg-[var(--color-error)]/10 p-3 text-xs">
          <p className="mb-1 font-bold text-[var(--color-error)]">
            {t(Object.keys(brandErrors).length === 1 ? 'brandStudio.fixOne' : 'brandStudio.fixMany', { count: Object.keys(brandErrors).length })}
          </p>
          <ul className="list-disc pl-5 text-[var(--color-fg-primary)]">
            {Object.entries(brandErrors).map(([k, msg]) => (
              <li key={k}>{msg}</li>
            ))}
          </ul>
        </div>
      )}

      {/* ── Split panel ─────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(360px,420px)]">
        {/* LEFT — tabs + form */}
        <div className="rounded-xl border border-[var(--color-xk-border)] bg-[var(--color-xk-surface)] p-6">
          <Tabs value={tab} onValueChange={(v) => setTab(v as TabId)} className="w-full">
            <TabsList className="flex h-auto flex-wrap gap-1 rounded-lg p-1">
              {TAB_LABELS
                // Experience tab fields live on co_brandkits and aren't wired
                // through brandsApi.update — hide it in edit mode so users
                // don't change values that won't persist.
                .filter((tabName) => !(isEdit && tabName === 'Experience'))
                .map((tabName) => (
                  <TabsTrigger key={tabName} value={tabName} className="px-3 py-2 text-xs">
                    {t(TAB_LABEL_KEYS[tabName])}
                  </TabsTrigger>
                ))}
            </TabsList>

            <TabsContent value="Identity">
              <IdentityTab config={config} update={update} onUploadingChange={onUploadingChange} />
            </TabsContent>
            {!isEdit && (
              <TabsContent value="Experience">
                <ExperienceTab config={config} update={update} />
              </TabsContent>
            )}
            <TabsContent value="Look & Feel">
              <LookFeelTab config={config} update={update} />
            </TabsContent>
            <TabsContent value="Game">
              <GameTab config={config} update={update} />
            </TabsContent>
            <TabsContent value="Output">
              <OutputTab config={config} update={update} />
            </TabsContent>
          </Tabs>
        </div>

        {/* RIGHT — live preview */}
        <aside>
          <div className="sticky top-20">
            <div className="mb-3 flex items-center justify-between">
              <h2
                className="text-[11px] font-extrabold uppercase tracking-widest text-[var(--color-xk-text-muted)]"
                style={{ fontFamily: 'var(--font-display)' }}
              >
                {t('brandStudio.livePreview')}
              </h2>
              <span
                className="rounded bg-[var(--color-accent-15)] px-2 py-0.5 text-[9px] font-extrabold uppercase tracking-widest text-[var(--color-xk-accent)]"
                style={{ fontFamily: 'var(--font-display)' }}
              >
                {t(TAB_LABEL_KEYS[tab])}
              </span>
            </div>
            <BrandPreview config={config} />
            <p className="mt-3 text-[11px] text-[var(--color-xk-text-muted)]">
              {t('brandStudio.previewFooter')}
            </p>
          </div>
        </aside>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// Tab components
// ════════════════════════════════════════════════════════════════════════════

interface TabBaseProps {
  config: BrandStudioConfig;
  update: (delta: Partial<BrandStudioConfig>) => void;
}

function IdentityTab({
  config,
  update,
  onUploadingChange,
}: TabBaseProps & { onUploadingChange?: (uploading: boolean) => void }) {
  const t = useT();
  return (
    <div className="space-y-5">
      <Hint message={t('brandStudio.identity.hint')} />

      <Field
        label={t('brandStudio.identity.brandName')}
        hint={t('brandStudio.identity.brandNameHint')}
      >
        <input
          type="text"
          value={config.name}
          onChange={(e) => update({ name: e.target.value })}
          placeholder={t('brandStudio.identity.brandNamePlaceholder')}
          autoFocus
          className={inputCls}
        />
      </Field>

      <Field
        label={t('brandStudio.identity.industry')}
        hint={t('brandStudio.identity.industryHint')}
      >
        <input
          type="text"
          value={config.industry}
          onChange={(e) => update({ industry: e.target.value })}
          placeholder={t('brandStudio.identity.industryPlaceholder')}
          className={inputCls}
        />
      </Field>

      <Field
        label={t('brandStudio.identity.campaignName')}
        hint={t('brandStudio.identity.campaignNameHint')}
      >
        <input
          type="text"
          value={config.campaignName}
          onChange={(e) => update({ campaignName: e.target.value })}
          placeholder={t('brandStudio.identity.campaignNamePlaceholder')}
          className={inputCls}
        />
      </Field>

      <Field label={t('brandStudio.identity.brandLogo')} hint={t('brandStudio.identity.brandLogoHint')}>
        <ImagePicker
          value={config.brandLogo}
          onChange={(v) => update({ brandLogo: v })}
          slotLabel={t('brandStudio.identity.noLogo')}
          preset="logo"
          onUploadingChange={onUploadingChange}
        />
      </Field>

      {/* QR thumbnail is configured per-event (bs_event.qrThumbnail), not at
          the brand level. Hidden here so users don't think this picker
          drives the actual QR rendered in surveys — that decision lives
          on the event page. */}

      <Field
        label={t('brandStudio.identity.backgroundImage')}
        hint={t('brandStudio.identity.backgroundImageHint')}
      >
        <ImagePicker
          value={config.backgroundImage}
          onChange={(v) => update({ backgroundImage: v })}
          slotLabel={t('brandStudio.identity.noImage')}
          preset="photo"
          onUploadingChange={onUploadingChange}
        />
      </Field>

      {/* 5-color palette */}
      <div>
        <SectionLabel>{t('brandStudio.identity.palette')}</SectionLabel>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <ColorField
            label={t('brandStudio.identity.color.primary')}
            description={t('brandStudio.identity.color.primaryDesc')}
            value={config.primaryColor}
            onChange={(v) => update({ primaryColor: v })}
          />
          <ColorField
            label={t('brandStudio.identity.color.secondary')}
            description={t('brandStudio.identity.color.secondaryDesc')}
            value={config.secondaryColor}
            onChange={(v) => update({ secondaryColor: v })}
          />
          <ColorField
            label={t('brandStudio.identity.color.accent')}
            description={t('brandStudio.identity.color.accentDesc')}
            value={config.accentColor}
            onChange={(v) => update({ accentColor: v })}
          />
          <ColorField
            label={t('brandStudio.identity.color.background')}
            description={t('brandStudio.identity.color.backgroundDesc')}
            value={config.backgroundColor}
            onChange={(v) => update({ backgroundColor: v })}
          />
          <ColorField
            label={t('brandStudio.identity.color.text')}
            description={t('brandStudio.identity.color.textDesc')}
            value={config.textColor}
            onChange={(v) => update({ textColor: v })}
          />
        </div>
      </div>

      <Field label={t('brandStudio.identity.brandFont')}>
        <select
          value={config.font}
          onChange={(e) => update({ font: e.target.value })}
          className={inputCls}
          style={{ fontFamily: `"${config.font}", sans-serif` }}
        >
          {FONTS.map((f) => (
            <option key={f} value={f} style={{ fontFamily: `"${f}", sans-serif` }}>
              {f}
            </option>
          ))}
        </select>
      </Field>
    </div>
  );
}

function PaidGateBadge({ enabled }: { enabled: boolean }) {
  if (enabled) return null;
  return (
    <span className="ml-2 rounded-full bg-[var(--color-accent-15)] px-2 py-0.5 text-[9px] font-extrabold uppercase tracking-widest text-[var(--color-xk-accent)]">
      PAID
    </span>
  );
}

function ExperienceTab({ config, update }: TabBaseProps) {
  const t = useT();
  const hasEn = config.language === 'en' || config.language === 'en-es';
  const hasEs = config.language === 'es' || config.language === 'en-es';
  const toggleLang = (l: 'en' | 'es') => {
    const next = { en: l === 'en' ? !hasEn : hasEn, es: l === 'es' ? !hasEs : hasEs };
    if (!next.en && !next.es) return;
    update({ language: next.en && next.es ? 'en-es' : next.en ? 'en' : 'es' });
  };
  return (
    <div className="space-y-5">
      <Hint message={t('brandStudio.experience.hint')} />

      <div>
        <SectionLabel>{t('brandStudio.experience.surveyLanguage')}</SectionLabel>
        <div className="rounded-lg border border-[var(--color-xk-border)] bg-[var(--color-xk-bg-deep)] p-3 text-sm text-[var(--color-xk-lavender)]">
          <label className="mr-6 inline-flex items-center gap-2">
            <input type="checkbox" checked={hasEn} onChange={() => toggleLang('en')} />
            {t('brandStudio.experience.english')}
          </label>
          <label className="inline-flex items-center gap-2">
            <input type="checkbox" checked={hasEs} onChange={() => toggleLang('es')} />
            {t('brandStudio.experience.spanish')}
          </label>
          {config.language === 'en-es' && (
            <p className="mt-1.5 text-[11px] font-bold text-[var(--color-xk-accent)]">
              {t('brandStudio.experience.bilingualHint')}
            </p>
          )}
        </div>
      </div>

      <div>
        <SectionLabel>{t('brandStudio.experience.contactInfo')}</SectionLabel>
        <div className="rounded-lg border border-[var(--color-xk-border)] bg-[var(--color-xk-bg-deep)] p-3 text-sm text-[var(--color-xk-lavender)]">
          <Toggle
            label={t('brandStudio.experience.collectContact')}
            checked={config.collectData}
            onChange={(v) => update({ collectData: v })}
          />
          {config.collectData && (
            <div className="mt-2 grid grid-cols-1 gap-1 pl-5 sm:grid-cols-3">
              <Toggle
                label={t('brandStudio.experience.fullName')}
                checked={config.collectName}
                onChange={(v) => update({ collectName: v })}
              />
              <Toggle
                label={t('brandStudio.experience.email')}
                checked={config.collectEmail}
                onChange={(v) => update({ collectEmail: v })}
              />
              <Toggle
                label={t('brandStudio.experience.phone')}
                checked={config.collectPhone}
                onChange={(v) => update({ collectPhone: v })}
              />
            </div>
          )}
        </div>
      </div>

      <div>
        <SectionLabel>{t('brandStudio.experience.ageGate')}</SectionLabel>
        <div className="flex gap-2">
          {AGE_GATES.map((a) => (
            <PillOption
              key={a}
              active={config.ageGate === a}
              onClick={() => update({ ageGate: a })}
            >
              {a === 'none' ? t('brandStudio.experience.disabled') : a}
            </PillOption>
          ))}
        </div>
      </div>

      <div>
        <SectionLabel>{t('brandStudio.experience.demographics')}</SectionLabel>
        <DemographicsToggles config={config} update={update} />
      </div>

      <div>
        <SectionLabel>{t('brandStudio.experience.customQuestions')}</SectionLabel>
        <CustomQuestionsQuota config={config} update={update} />
      </div>
    </div>
  );
}

function DemographicsToggles({
  config,
  update,
}: {
  config: BrandStudioConfig;
  update: (patch: Partial<BrandStudioConfig>) => void;
}) {
  const t = useT();
  // PRD §6 — ethnicity + nationality are PAID-only data points. The toggle
  // stays clickable but writing `true` is a no-op until the plan flag is on,
  // so the manager can preview the gate without losing their selection on
  // plan upgrade.
  const ethnicityAccess = useFeatureAccess('ethnicityCollection');
  const nationalityAccess = useFeatureAccess('nationalityCollection');
  return (
    <div className="grid grid-cols-1 gap-1 rounded-lg border border-[var(--color-xk-border)] bg-[var(--color-xk-bg-deep)] p-3 sm:grid-cols-2">
      <Toggle
        label={t('brandStudio.experience.ageRange')}
        checked={config.demoAge}
        onChange={(v) => update({ demoAge: v })}
      />
      <Toggle
        label={t('brandStudio.experience.gender')}
        checked={config.demoGender}
        onChange={(v) => update({ demoGender: v })}
      />
      <Toggle
        label={t('brandStudio.experience.pronouns')}
        checked={config.demoPronouns}
        onChange={(v) => update({ demoPronouns: v })}
      />
      <Toggle
        label={
          <>
            {t('brandStudio.experience.ethnicity')}
            <PaidGateBadge enabled={ethnicityAccess.enabled} />
          </>
        }
        checked={config.demoEthnicity}
        onChange={(v) => {
          if (!ethnicityAccess.enabled && v) return;
          update({ demoEthnicity: v });
        }}
      />
      <Toggle
        label={
          <>
            {t('brandStudio.experience.nationality')}
            <PaidGateBadge enabled={nationalityAccess.enabled} />
          </>
        }
        checked={config.demoNationality}
        onChange={(v) => {
          if (!nationalityAccess.enabled && v) return;
          update({ demoNationality: v });
        }}
      />
    </div>
  );
}

function CustomQuestionsQuota({
  config,
  update,
}: {
  config: BrandStudioConfig;
  update: (patch: Partial<BrandStudioConfig>) => void;
}) {
  const t = useT();
  // PRD §6 — custom-question quota is 0/2/5/5/inf for Pilot/Scale/Program/
  // Enterprise/etc. The boolean flag `customQuestions` gates "any at all";
  // a follow-up Plan caps field carries the integer ceiling. Until that
  // lands the chip surfaces a generic upgrade hint.
  const access = useFeatureAccess('customQuestions');
  const count = config.customQuestionsCount;
  return (
    <div className="rounded-lg border border-[var(--color-xk-border)] bg-[var(--color-xk-bg-deep)] p-3">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-sm text-[var(--color-xk-lavender)]">
            {t('brandStudio.experience.customQuestionsLabel', { count })}
          </p>
          <p className="mt-1 text-[11px] text-[var(--color-xk-text-muted)]">
            {access.enabled
              ? t('brandStudio.experience.customQuestionsAccessOn')
              : t('brandStudio.experience.customQuestionsAccessOff')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            disabled={!access.enabled || count <= 0}
            onClick={() => update({ customQuestionsCount: Math.max(0, count - 1) })}
            className="rounded-md border border-[var(--color-xk-border)] px-3 py-1 text-sm font-bold text-[var(--color-xk-lavender)] disabled:opacity-40"
          >
            −
          </button>
          <span className="w-6 text-center text-sm font-bold text-[var(--color-xk-lavender)]">
            {count}
          </span>
          <button
            type="button"
            disabled={!access.enabled}
            onClick={() => update({ customQuestionsCount: count + 1 })}
            className="rounded-md border border-[var(--color-xk-border)] px-3 py-1 text-sm font-bold text-[var(--color-xk-lavender)] disabled:opacity-40"
          >
            +
          </button>
        </div>
      </div>
    </div>
  );
}

function LookFeelTab({ config, update }: TabBaseProps) {
  const t = useT();
  // Caso 05 v1 — "Test celebration" simulator. Re-mount via a token bump so
  // consecutive presses re-trigger the animation reliably.
  const [simToken, setSimToken] = useState(0);
  // PRD §13.5 — TCPA gate before flipping to Variant B.
  const [tcpaModalOpen, setTcpaModalOpen] = useState(false);
  const handleVariantSelect = async (v: 'A' | 'B') => {
    // Variant A or already-active B path: no legal check needed.
    if (v === 'A' || config.consentVariant === 'B') {
      update({ consentVariant: v });
      return;
    }
    // Switching to B for the first time → require TCPA acceptance.
    try {
      const existing = await legalApi.check(TCPA_ACK.type, TCPA_ACK.versionHash);
      if (existing) {
        update({ consentVariant: 'B' });
        return;
      }
    } catch {
      // Fail open — show the modal anyway. Backend idempotency makes
      // duplicate acceptance harmless.
    }
    setTcpaModalOpen(true);
  };
  return (
    <div className="space-y-5">
      <Hint message={t('brandStudio.look.hint')} />

      <div>
        <SectionLabel>{t('brandStudio.look.buttonStyle')}</SectionLabel>
        <div className="grid grid-cols-3 gap-2">
          {BUTTON_STYLES.map((s) => {
            const active = config.buttonStyle === s;
            return (
              <button
                key={s}
                type="button"
                onClick={() => update({ buttonStyle: s })}
                className={`rounded-lg border p-3 text-center transition-colors ${
                  active
                    ? 'border-[var(--color-xk-accent)] bg-[var(--color-accent-06)]'
                    : 'border-[var(--color-xk-border)] hover:border-[var(--color-xk-accent)]'
                }`}
              >
                <span
                  className="mb-1.5 inline-block px-3 py-1 text-[11px] font-extrabold"
                  style={{
                    backgroundColor: config.accentColor,
                    color: '#0A0A0A',
                    borderRadius: s === 'pill' ? 999 : s === 'square' ? 4 : 8,
                  }}
                >
                  {t('brandStudio.look.next')}
                </span>
                <div className="text-[11px] font-bold capitalize text-[var(--color-xk-lavender)]">
                  {s}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <div className="relative">
        <div className="mb-2 flex items-center justify-between">
          <SectionLabel>{t('brandStudio.look.celebration')}</SectionLabel>
          {/* Caso 05 v1 — Test the currently selected celebration. Hidden
              when `none` since there's nothing to render. */}
          {config.celebrationTheme !== 'none' && (
            <button
              type="button"
              onClick={() => setSimToken((n) => n + 1)}
              className="inline-flex items-center gap-1 rounded-md border border-[var(--color-xk-border)] px-3 py-1 text-xs font-semibold hover:border-[var(--color-xk-accent)] hover:text-[var(--color-xk-accent)]"
            >
              <Play className="h-3 w-3" />
              {t('brandStudio.game.test')}
            </button>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {CELEBRATIONS.map((c) => (
            <PillOption
              key={c}
              active={config.celebrationTheme === c}
              onClick={() => update({ celebrationTheme: c })}
            >
              {c === 'none' ? t('brandStudio.look.noAnimation') : c.charAt(0).toUpperCase() + c.slice(1)}
            </PillOption>
          ))}
        </div>
        {simToken > 0 && config.celebrationTheme !== 'none' && (
          <CelebrationOverlay
            key={simToken}
            theme={
              // Map local BrandStudio celebration value to the package's
              // accepted set (the local list has extra entries that
              // CelebrationOverlay treats as confetti).
              (config.celebrationTheme === 'fireworks' ||
              config.celebrationTheme === 'sparks'
                ? config.celebrationTheme
                : 'confetti') as 'confetti' | 'fireworks' | 'sparks'
            }
            accent={config.secondaryColor}
          />
        )}
      </div>

      <Field
        label={t('brandStudio.look.consent')}
        hint={t('brandStudio.look.consentHint')}
      >
        <textarea
          rows={3}
          value={config.consentWording}
          onChange={(e) => update({ consentWording: e.target.value })}
          className={`${inputCls} resize-y`}
        />
      </Field>

      <Field
        label={t('brandStudio.look.consentVariant')}
        hint={t('brandStudio.look.consentVariantHint')}
      >
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {(['A', 'B'] as const).map((v) => {
            const active = config.consentVariant === v;
            return (
              <button
                key={v}
                type="button"
                onClick={() => handleVariantSelect(v)}
                className={`rounded-lg border p-3 text-left transition-colors ${
                  active
                    ? 'border-[var(--color-xk-accent)] bg-[var(--color-accent-06)]'
                    : 'border-[var(--color-xk-border)] hover:border-[var(--color-xk-accent)]'
                }`}
              >
                <div className="text-xs font-bold text-[var(--color-xk-lavender)]">
                  {v === 'A'
                    ? t('brandStudio.look.consentVariantA')
                    : t('brandStudio.look.consentVariantB')}
                </div>
                <div className="mt-1 text-[11px] text-[var(--color-xk-text-muted)]">
                  {v === 'A'
                    ? t('brandStudio.look.consentVariantADesc')
                    : t('brandStudio.look.consentVariantBDesc')}
                </div>
              </button>
            );
          })}
        </div>
      </Field>

      <Field
        label={t('brandStudio.look.atlediTone')}
        hint={t('brandStudio.look.atlediToneHint')}
      >
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
          {(['playful', 'professional', 'warm', 'bold', 'custom'] as const).map(
            (tone) => {
              const active = config.atlediTone === tone;
              return (
                <button
                  key={tone}
                  type="button"
                  onClick={() => update({ atlediTone: tone })}
                  className={`rounded-lg border p-2 text-left transition-colors ${
                    active
                      ? 'border-[var(--color-xk-accent)] bg-[var(--color-accent-06)]'
                      : 'border-[var(--color-xk-border)] hover:border-[var(--color-xk-accent)]'
                  }`}
                >
                  <p className="text-[11px] font-bold text-[var(--color-xk-lavender)]">
                    {t(`brandStudio.look.atlediTone.${tone}`)}
                  </p>
                </button>
              );
            },
          )}
        </div>
        {config.atlediTone === 'custom' && (
          <textarea
            rows={2}
            value={config.atlediToneCustom}
            onChange={(e) => update({ atlediToneCustom: e.target.value })}
            placeholder={t('brandStudio.look.atlediToneCustomPlaceholder')}
            className={`${inputCls} mt-2 resize-y`}
          />
        )}
      </Field>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label={t('brandStudio.look.gameCTAEn')}>
          <input
            type="text"
            value={config.gameCTAEn}
            onChange={(e) => update({ gameCTAEn: e.target.value })}
            placeholder={t('brandStudio.look.gameCTAEnPlaceholder')}
            className={inputCls}
          />
        </Field>
        <Field label={t('brandStudio.look.gameCTAEs')}>
          <input
            type="text"
            value={config.gameCTAEs}
            onChange={(e) => update({ gameCTAEs: e.target.value })}
            placeholder={t('brandStudio.look.gameCTAEsPlaceholder')}
            className={inputCls}
          />
        </Field>
      </div>

      {/* PRD §13.5 — TCPA modal. Renders only when the manager toggled to
          Variant B without a prior acceptance row for the current hash. */}
      {tcpaModalOpen && (
        <LegalAcceptanceModal
          document={TCPA_ACK}
          onDone={() => {
            setTcpaModalOpen(false);
            update({ consentVariant: 'B' });
          }}
          onDismiss={() => setTcpaModalOpen(false)}
        />
      )}
    </div>
  );
}

function GameTab({ config, update }: TabBaseProps) {
  const t = useT();
  // Plan's `allowedGames` is comma-separated (e.g. "wheel,scratch,trivia").
  // Picker enables only those IDs; everything else renders locked with a hint
  // to upgrade. Wheel always available as a fallback so the picker is never
  // empty (and so we don't gate during the brief moment before /plans/current
  // resolves).
  const { data: plan } = useCurrentPlan();
  const allowed = new Set(
    (plan?.allowedGames ?? 'wheel')
      .split(',')
      .map((s: string) => s.trim())
      .filter(Boolean),
  );
  const unlocked = (id: string) => allowed.has(id);
  // PRD §18 mockup-driven picker: 3-col grid of cards with animated previews
  // + sticky detail panel showing a large preview + spec for the selected
  // game. The detail panel reads from the same GAMES array so adding a new
  // game requires no UI changes here.
  const selected = GAMES.find((g) => g.id === config.gameMechanic) ?? GAMES[0];
  const primary = config.primaryColor || '#8B5CF6';
  const accent = config.accentColor || '#C6D93B';
  return (
    <div className="space-y-5">
      <Hint message="Each game showcases your brand differently. Wheel ships with all plans; premium and pro games unlock with upgrades." />
      <div className="grid gap-4 lg:grid-cols-[1fr_280px]">
        {/* ── Grid of mini cards ───────────────────────────────────── */}
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          {GAMES.map((g) => {
            const isOpen = unlocked(g.id);
            const isSelected = config.gameMechanic === g.id;
            return (
              <div
                key={g.id}
                role="button"
                tabIndex={isOpen ? 0 : -1}
                aria-disabled={!isOpen}
                aria-pressed={isSelected}
                onClick={() => isOpen && update({ gameMechanic: g.id })}
                onKeyDown={(e) => {
                  if (isOpen && (e.key === 'Enter' || e.key === ' ')) {
                    e.preventDefault();
                    update({ gameMechanic: g.id });
                  }
                }}
                className={`group relative rounded-xl border p-3 text-left transition-colors ${
                  isSelected
                    ? 'border-[var(--color-xk-accent)] bg-[var(--color-accent-06)]'
                    : 'border-[var(--color-xk-border)] hover:border-[var(--color-xk-accent)]'
                } ${!isOpen ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
              >
                {isSelected && (
                  <span className="absolute right-2 top-2 grid h-5 w-5 place-items-center rounded-full bg-[var(--color-xk-accent)]">
                    <Check className="h-3 w-3 text-[var(--color-xk-bg-deep)]" />
                  </span>
                )}
                {!isOpen && (
                  <Lock className="absolute right-2 top-2 h-3.5 w-3.5 text-[var(--color-xk-text-muted)]" />
                )}
                <div className="mb-2 flex h-[80px] items-center justify-center">
                  <GamePreview code={g.id} size={80} primary={primary} accent={accent} />
                </div>
                <div className="flex items-center gap-2">
                  <span
                    className="text-[13px] font-extrabold text-[var(--color-xk-lavender)]"
                    style={{ fontFamily: 'var(--font-display)' }}
                  >
                    {t(g.nameKey)}
                  </span>
                </div>
                <p className="mt-0.5 line-clamp-2 text-[10.5px] text-[var(--color-xk-text-muted)]">
                  {t(g.descKey)}
                </p>
                <div className="mt-1.5">
                  <TierBadge tier={g.tier} />
                </div>
              </div>
            );
          })}
        </div>
        {/* ── Sticky detail panel ──────────────────────────────────── */}
        <aside className="rounded-xl border border-[var(--color-xk-border)] bg-[var(--color-xk-bg-deep)] p-4">
          <div className="mb-1 text-[10px] font-extrabold uppercase tracking-widest text-[var(--color-xk-text-muted)]">
            {t('brandStudio.gamePicker.selectedGame')}
          </div>
          <h3
            className="mb-3 text-[15px] font-extrabold text-[var(--color-xk-lavender)]"
            style={{ fontFamily: 'var(--font-display)' }}
          >
            {t(selected.nameKey)}
          </h3>
          <div className="mb-3 flex items-center justify-center rounded-lg bg-black/40 p-4">
            <GamePreview code={selected.id} size={160} primary={primary} accent={accent} />
          </div>
          <p className="mb-3 text-[12px] text-[var(--color-xk-text-muted)]">
            {t(selected.descKey)}
          </p>
          <div className="mb-1 text-[10px] font-extrabold uppercase tracking-widest text-[var(--color-xk-accent)]">
            {t('brandStudio.brandIntegration')}
          </div>
          <ul className="space-y-0.5 text-[11px] text-[var(--color-xk-text-muted)]">
            {selected.specKeys.map((key) => (
              <li key={key}>· {t(key)}</li>
            ))}
          </ul>
          <div className="mt-3 flex items-center justify-between text-[10px] text-[var(--color-xk-text-muted)]">
            <span>{t('brandStudio.gamePicker.tierRequired')}</span>
            <TierBadge tier={selected.tier} />
          </div>
        </aside>
      </div>

      {config.gameMechanic === 'wheel' && (
        <>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Wheel tagline (EN)">
              <input
                type="text"
                value={config.wheelTaglineEn}
                onChange={(e) => update({ wheelTaglineEn: e.target.value })}
                placeholder="Spin to win!"
                className={inputCls}
              />
            </Field>
            <Field label="Wheel tagline (ES)">
              <input
                type="text"
                value={config.wheelTaglineEs}
                onChange={(e) => update({ wheelTaglineEs: e.target.value })}
                placeholder="¡Gira para ganar!"
                className={inputCls}
              />
            </Field>
          </div>
          <Field
            label="Wheel hero image"
            hint="Optional. Product shot or event banner shown behind the wheel."
          >
            <ImagePicker
              value={config.wheelHeroImage}
              onChange={(v) => update({ wheelHeroImage: v })}
              slotLabel="No hero"
            />
          </Field>
        </>
      )}
    </div>
  );
}
function OutputTab({
  config,
  update,
}: {
  config: BrandStudioConfig;
  update: (patch: Partial<BrandStudioConfig>) => void;
}) {
  const [copied, setCopied] = useState(false);
  const slug = useMemo(
    () =>
      (config.campaignName || config.name || 'brand')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '')
        .slice(0, 60),
    [config.campaignName, config.name],
  );
  const link = `xotski.com/s/${slug}`;
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(`https://${link}`);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = `https://${link}`;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div className="space-y-5">
      <Hint message="Each station gets its own unique QR — brand colors and logo configured here apply automatically." />

      <div className="text-center">
        <SectionLabel>Universal QR code</SectionLabel>
        <div
          className="mx-auto inline-flex flex-col items-center rounded-2xl p-6"
          style={{ backgroundColor: 'var(--color-xk-bg-deep)' }}
        >
          <div
            className="grid h-44 w-44 place-items-center rounded-lg p-4"
            style={{ backgroundColor: config.primaryColor }}
          >
            <div
              className="relative h-full w-full rounded"
              style={{
                background: `repeating-conic-gradient(${config.accentColor} 0% 25%, transparent 0% 50%) 0 0 / 18px 18px, ${config.primaryColor}`,
                opacity: 0.85,
              }}
            >
              {(config.qrThumbnail || config.brandLogo) && (
                <div
                  className="absolute left-1/2 top-1/2 grid h-10 w-10 -translate-x-1/2 -translate-y-1/2 place-items-center rounded p-1"
                  style={{ backgroundColor: config.primaryColor }}
                >
                  <img
                    src={resolveImageUrl((config.qrThumbnail || config.brandLogo) as string)}
                    alt=""
                    className="max-h-full max-w-full object-contain"
                  />
                </div>
              )}
            </div>
          </div>
          <p className="mt-3 text-[11px] text-[var(--color-xk-text-muted)]">
            {config.brandLogo ? 'QR includes your embedded logo' : 'Upload a logo to embed it in the QR'}
          </p>
        </div>
      </div>

      <Field label="Shareable link">
        <div className="flex items-center gap-2 rounded-lg border border-[var(--color-xk-border)] bg-[var(--color-xk-bg-deep)] p-2.5">
          <code
            className="flex-1 truncate text-[13px] font-semibold text-[var(--color-xk-accent)]"
            style={{ fontFamily: 'var(--font-display)' }}
          >
            {link}
          </code>
          <button
            type="button"
            onClick={onCopy}
            className={`inline-flex items-center gap-1 rounded-md px-2.5 py-1 text-[11px] font-bold ${
              copied
                ? 'bg-[var(--color-accent-15)] text-[var(--color-xk-accent)]'
                : 'border border-[var(--color-xk-border)] text-[var(--color-xk-lavender)] hover:bg-[var(--color-xk-surface)]'
            }`}
          >
            {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
            {copied ? 'Copied' : 'Copy'}
          </button>
        </div>
      </Field>

      <div className="rounded-lg border border-[var(--color-accent-20)] bg-[var(--color-accent-06)] p-4">
        <p className="mb-1 text-[12px] font-bold text-[var(--color-xk-lavender)]">
          Each station gets its own unique link & QR
        </p>
        <p className="text-[12px] leading-relaxed text-[var(--color-xk-text-muted)]">
          Public links are generated per station — not per brand. Go to{' '}
          <strong className="text-[var(--color-xk-accent)]">Projects → Event → Station</strong> to
          download individual QR codes. The brand settings configured here apply automatically.
        </p>
      </div>

      <div className="flex flex-wrap justify-center gap-2">
        <button
          type="button"
          disabled
          className="inline-flex cursor-not-allowed items-center gap-1.5 rounded-lg bg-[var(--color-accent-30)] px-4 py-2 text-xs font-extrabold text-[rgb(26_26_46/0.5)]"
          title="Available after saving + wiring to a station"
        >
          <Download className="h-3.5 w-3.5" />
          Download PNG
        </button>
        <button
          type="button"
          disabled
          className="inline-flex cursor-not-allowed items-center gap-1.5 rounded-lg border border-[var(--color-xk-border)] px-4 py-2 text-xs font-bold text-[var(--color-xk-text-muted)]"
        >
          <Printer className="h-3.5 w-3.5" />
          Print display
        </button>
      </div>

      {/* PRD §11 Screen 6 — Post-claim redirect. Controls what happens once
          the consumer reveals their claim code: stay on Onxite, show a
          branded thank-you, or hand off to a sponsor microsite. */}
      <div>
        <SectionLabel>Post-claim redirect</SectionLabel>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
          {(
            [
              {
                v: 'none' as const,
                label: 'Onxite finish',
                desc: 'Default thank-you screen.',
              },
              {
                v: 'branded' as const,
                label: 'Branded',
                desc: 'Stay in Xotski with a brand-themed message.',
              },
              {
                v: 'external' as const,
                label: 'External URL',
                desc: 'Send winners to a sponsor link.',
              },
            ]
          ).map((opt) => {
            const active = config.postClaimRedirect === opt.v;
            return (
              <button
                key={opt.v}
                type="button"
                onClick={() => update({ postClaimRedirect: opt.v })}
                className={`rounded-lg border p-3 text-left transition-colors ${
                  active
                    ? 'border-[var(--color-xk-accent)] bg-[var(--color-accent-06)]'
                    : 'border-[var(--color-xk-border)] hover:border-[var(--color-xk-accent)]'
                }`}
              >
                <div className="text-xs font-bold text-[var(--color-xk-lavender)]">
                  {opt.label}
                </div>
                <div className="mt-1 text-[11px] text-[var(--color-xk-text-muted)]">
                  {opt.desc}
                </div>
              </button>
            );
          })}
        </div>
        {config.postClaimRedirect === 'external' && (
          <div className="mt-3">
            <Field
              label="Redirect URL"
              hint="Full https:// link. Opens after the claim-code screen."
            >
              <input
                type="url"
                value={config.postClaimRedirectUrl}
                onChange={(e) => update({ postClaimRedirectUrl: e.target.value })}
                placeholder="https://brand.example.com/thanks"
                className={inputCls}
              />
            </Field>
          </div>
        )}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// Shared subcomponents + styles
// ════════════════════════════════════════════════════════════════════════════

const inputCls =
  'w-full rounded-lg border border-[var(--color-xk-border)] bg-[var(--color-xk-bg-deep)] px-3 py-2.5 text-sm text-[var(--color-xk-lavender)] placeholder:text-[var(--color-xk-text-muted)] focus:border-[var(--color-xk-accent)] focus:outline-none';

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <div className="mb-1.5 text-[11px] font-extrabold uppercase tracking-widest text-[var(--color-xk-text-muted)]">
        {label}
      </div>
      {children}
      {hint && <div className="mt-1 text-[11px] text-[var(--color-xk-text-muted)]">{hint}</div>}
    </label>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="mb-2 text-[11px] font-extrabold uppercase tracking-widest text-[var(--color-xk-text-muted)]">
      {children}
    </div>
  );
}

function ColorField({
  label,
  description,
  value,
  onChange,
}: {
  label: string;
  description?: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="rounded-lg border border-[var(--color-xk-border)] bg-[var(--color-xk-bg-deep)] p-3">
      <div className="mb-1.5 text-[11px] font-extrabold uppercase tracking-widest text-[var(--color-xk-text-muted)]">
        {label}
      </div>
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="h-8 w-10 cursor-pointer rounded border border-[var(--color-xk-border)] bg-transparent"
        />
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full bg-transparent text-[12px] uppercase tabular-nums text-[var(--color-xk-lavender)] focus:outline-none"
          style={{ fontFamily: 'var(--font-display)' }}
        />
      </div>
      {description && (
        <p className="mt-1 text-[10px] text-[var(--color-xk-text-muted)]">{description}</p>
      )}
    </div>
  );
}

function ImagePicker({
  value,
  onChange,
  slotLabel,
  preset = 'photo',
  onUploadingChange,
}: {
  value: string | null;
  onChange: (v: string | null) => void;
  slotLabel: string;
  preset?: CompressPreset;
  onUploadingChange?: (uploading: boolean) => void;
}) {
  const ref = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  // Upload to /uploads instead of stashing the data URI in the value. The
  // `co_companies.logo` / `backgroundImage` columns are varchar(255) and a
  // base64 image overflows them immediately — see the "Data too long for
  // column 'logo'" incident. Now the column only ever stores the returned URL.
  const onFile = async (file: File) => {
    setUploading(true);
    onUploadingChange?.(true);
    try {
      const res = await uploadImage(file, { preset });
      onChange(res.url);
    } catch (err: any) {
      toast({
        tone: 'error',
        title: 'Upload failed',
        description: err?.message ?? 'Could not upload the image.',
      });
    } finally {
      setUploading(false);
      onUploadingChange?.(false);
      if (ref.current) ref.current.value = '';
    }
  };

  return (
    <div className="flex items-center gap-3">
      <div className="grid h-16 w-16 flex-none place-items-center overflow-hidden rounded-lg border border-[var(--color-xk-border)] bg-[var(--color-xk-bg-deep)]">
        {value ? (
          <img src={resolveImageUrl(value)} alt="" className="h-full w-full object-contain p-2" />
        ) : (
          <span className="text-[10px] text-[var(--color-xk-text-muted)]">{slotLabel}</span>
        )}
      </div>
      <input
        ref={ref}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/svg+xml"
        onChange={(e) => e.target.files?.[0] && void onFile(e.target.files[0])}
        className="hidden"
        disabled={uploading}
      />
      <button
        type="button"
        onClick={() => ref.current?.click()}
        disabled={uploading}
        className="inline-flex items-center gap-1.5 rounded-lg border border-[var(--color-xk-border)] px-3 py-2 text-xs font-bold text-[var(--color-xk-lavender)] hover:bg-[var(--color-xk-bg-deep)] disabled:cursor-not-allowed disabled:opacity-60"
      >
        {uploading ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
        ) : (
          <Upload className="h-3.5 w-3.5" />
        )}
        {uploading ? 'Uploading…' : 'Upload'}
      </button>
      {value && !uploading && (
        <button
          type="button"
          onClick={() => onChange(null)}
          className="grid h-7 w-7 place-items-center rounded-md text-[var(--color-xk-text-muted)] hover:bg-[var(--color-xk-bg-deep)] hover:text-[var(--color-xk-lavender)]"
          title="Clear"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}

function Toggle({
  label,
  checked,
  onChange,
}: {
  label: React.ReactNode;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="inline-flex cursor-pointer items-center gap-2 py-1 text-[13px] text-[var(--color-xk-lavender)]">
      <span
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={`relative h-4 w-7 rounded-full transition-colors ${
          checked ? 'bg-[var(--color-xk-accent)]' : 'bg-[var(--color-xk-border)]'
        }`}
      >
        <span
          className={`absolute top-0.5 h-3 w-3 rounded-full bg-white transition-all ${
            checked ? 'left-3.5' : 'left-0.5'
          }`}
        />
      </span>
      {label}
    </label>
  );
}

function PillOption({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-lg border px-3 py-1.5 text-xs font-bold transition-colors ${
        active
          ? 'border-[var(--color-xk-accent)] bg-[var(--color-accent-10)] text-[var(--color-xk-accent)]'
          : 'border-[var(--color-xk-border)] text-[var(--color-xk-lavender)] hover:border-[var(--color-xk-accent)]'
      }`}
    >
      {children}
    </button>
  );
}

function TierBadge({ tier }: { tier: GameInfo['tier'] }) {
  const map = {
    free: { bg: 'var(--color-accent-15)', fg: 'var(--color-xk-accent)', label: 'Free' },
    premium: { bg: 'rgb(255 179 0 / 0.15)', fg: '#FFB300', label: 'Premium' },
    pro: { bg: 'rgb(156 39 176 / 0.15)', fg: '#CE93D8', label: 'Pro' },
  } as const;
  const c = map[tier];
  return (
    <span
      className="rounded px-1.5 py-0.5 text-[9px] font-extrabold uppercase tracking-wider"
      style={{ backgroundColor: c.bg, color: c.fg, fontFamily: 'var(--font-display)' }}
    >
      {c.label}
    </span>
  );
}

function Hint({ message }: { message: string }) {
  return (
    <div className="flex items-start gap-2 rounded-lg border border-[var(--color-accent-20)] bg-[var(--color-accent-05)] px-3 py-2.5">
      <Sparkles className="mt-0.5 h-3.5 w-3.5 flex-none text-[var(--color-xk-accent)]" />
      <p className="text-[12px] leading-relaxed text-[var(--color-xk-lavender)]">{message}</p>
    </div>
  );
}

// ── Live preview ───────────────────────────────────────────────────────────

function BrandPreview({ config }: { config: BrandStudioConfig }) {
  const name = config.name.trim() || 'Your brand';
  const gradient = `linear-gradient(135deg, ${config.primaryColor} 0%, var(--color-xk-bg) 100%)`;
  const buttonRadius =
    config.buttonStyle === 'pill' ? 999 : config.buttonStyle === 'square' ? 4 : 8;
  return (
    <div className="overflow-hidden rounded-2xl border border-[var(--color-xk-border)]">
      <div className="relative h-32 px-5 pt-5" style={{ background: gradient }}>
        <div className="flex items-center gap-3">
          <div className="grid h-12 w-12 place-items-center rounded-lg bg-[rgb(0_0_0/0.3)]">
            {config.brandLogo ? (
              <img
                src={resolveImageUrl(config.brandLogo)}
                alt=""
                className="max-h-10 max-w-[40px] object-contain"
              />
            ) : (
              <span
                className="text-base font-black"
                style={{
                  color: config.textColor,
                  fontFamily: `"${config.font}", sans-serif`,
                }}
              >
                {name.charAt(0).toUpperCase()}
              </span>
            )}
          </div>
          <div>
            <div
              className="text-[10px] font-extrabold uppercase tracking-widest"
              style={{
                color: config.textColor,
                opacity: 0.7,
                fontFamily: 'var(--font-display)',
              }}
            >
              Brand workspace
            </div>
            <div
              className="text-lg font-black tracking-tight"
              style={{ color: config.textColor, fontFamily: `"${config.font}", sans-serif` }}
            >
              {name}
            </div>
          </div>
        </div>
      </div>

      <div className="bg-[var(--color-xk-surface)] p-5">
        <div
          className="mb-1 text-[10px] font-extrabold uppercase tracking-widest text-[var(--color-xk-text-muted)]"
          style={{ fontFamily: 'var(--font-display)' }}
        >
          {config.gameCTAEn || 'Spin to win'}
        </div>
        <div
          className="mb-4 text-xl font-black"
          style={{ color: config.textColor, fontFamily: `"${config.font}", sans-serif` }}
        >
          {config.campaignName.trim() || `${name} sampling`}
        </div>
        <button
          type="button"
          className="w-full px-4 py-2.5 text-sm font-extrabold transition-opacity hover:opacity-90"
          style={{
            backgroundColor: config.accentColor,
            color: '#0A0A0A',
            borderRadius: buttonRadius,
            fontFamily: `"${config.font}", sans-serif`,
          }}
        >
          {config.gameCTAEn || 'Continue'}
        </button>
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <Swatch color={config.primaryColor} label="Primary" />
          <Swatch color={config.secondaryColor} label="Secondary" />
          <Swatch color={config.accentColor} label="Accent" />
          <Swatch color={config.textColor} label="Text" />
        </div>
      </div>
    </div>
  );
}

function Swatch({ color, label }: { color: string; label: string }) {
  return (
    <div className="flex items-center gap-1">
      <span
        className="block h-3.5 w-3.5 rounded border border-[rgb(255_255_255/0.15)]"
        style={{ backgroundColor: color }}
      />
      <span className="text-[10px] text-[var(--color-xk-text-muted)]">{label}</span>
    </div>
  );
}
